<!-- Page Header with Dropdowns -->
<app-title-bar
  title="Analytics"
  description="Get insights of your car wash's performance and operations."
  [showButton]="false"
  [showDropdowns]="true"
>
  <!-- Custom Dropdown Content -->
  <ng-template #dropdownContent>
    <!-- Location Dropdown -->
    <app-simple-dropdown
      [options]="locationOptions"
      [selectedValue]="selectedLocationId"
      [title]="'Location'"
      [isOpen]="isLocationDropdownOpen"
      (selectionChange)="onLocationChange($event)"
      (toggle)="onLocationDropdownToggle()"
      (close)="onLocationDropdownClose()"
    >
    </app-simple-dropdown>

    <!-- Period Dropdown -->
    <div class="period-dropdown-wrapper">
      <button class="dropdown-button" (click)="toggleDateRangePicker(); $event.stopPropagation()">
        <span>{{ selectedPeriod }}</span>
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          [class.rotated]="isDateRangePickerOpen"
        >
          <path
            d="M5.28 7.38L10 12.1L14.72 7.38"
            stroke="#15172E"
            stroke-width="1"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>

      <!-- Date Range Picker Dropdown -->
      <app-date-range-picker
        [isOpen]="isDateRangePickerOpen"
        [selectedPeriod]="selectedPeriod"
        [customDateRange]="customDateRange"
        [periodOptions]="analyticsPeriodOptions"
        (periodChange)="onDateRangePeriodChange($event)"
        (customDateRangeChange)="onCustomDateRangeChange($event)"
        (close)="closeDateRangePicker()"
        (apply)="onDateRangeApply($event)"
      >
      </app-date-range-picker>
    </div>
  </ng-template>
</app-title-bar>

<!-- Analytics Stats Cards -->
<div class="analytics-stats-container">
  <!-- Always show stats cards with default/loading data -->
  <div class="analytics-stats-grid">
    @for (stat of analyticsStats; track trackByIndex($index)) {
      <app-analytics-stats-card [data]="stat"></app-analytics-stats-card>
    }
  </div>
</div>

<!-- Analytics Tabs -->
<app-tab-header [tabs]="tabItems" [activeTabId]="activeTab" (tabChange)="setActiveTab($event)">
</app-tab-header>

<!-- Charts Container -->
<div class="charts-container" [style.display]="activeTab === 'revenue' ? 'grid' : 'none'">
  <!-- Revenue Trend Chart -->
  <div class="revenue-trend-container">
    <div class="revenue-trend-card">
      <div class="card-header">
        <div class="header-content">
          <h3 class="card-title">Revenue Trend by Day</h3>
          <ng-container
            *ngTemplateOutlet="
              infoIcon;
              context: { tooltip: 'Daily revenue growth tracked every hour.' }
            "
          ></ng-container>
        </div>
      </div>
      <div class="chart-container">
        @if (hasRevenueTrendData) {
          <div class="line-chart-wrapper">
            <canvas
              #revenueChart="base-chart"
              baseChart
              type="line"
              [data]="revenueTrendData"
              [options]="revenueTrendOptions"
              class="line-chart"
            >
            </canvas>
          </div>
        }
        <ng-container
          *ngTemplateOutlet="noDataMessage; context: { show: !hasRevenueTrendData }"
        ></ng-container>
      </div>
    </div>
  </div>

  <!-- Revenue by Service Type Chart -->
  <div class="revenue-service-container">
    <div class="revenue-service-card">
      <div class="card-header">
        <div class="header-content">
          <h3 class="card-title">Revenue by Service Type</h3>
          <ng-container
            *ngTemplateOutlet="
              infoIcon;
              context: { tooltip: 'Revenue breakdown by different service types.' }
            "
          ></ng-container>
        </div>
      </div>
      <div class="chart-container">
        @if (hasServiceRevenueData) {
          <div class="donut-chart-wrapper">
            <canvas
              baseChart
              type="doughnut"
              [data]="serviceRevenueData"
              [options]="serviceRevenueOptions"
              class="donut-chart"
            >
            </canvas>
          </div>
        }
        <ng-container
          *ngTemplateOutlet="noDataMessage; context: { show: !hasServiceRevenueData }"
        ></ng-container>
      </div>
    </div>
  </div>
</div>

<!-- Services Tab Charts -->
<div
  class="services-charts-container"
  [style.display]="activeTab === 'services' ? 'block' : 'none'"
>
  <!-- Services Charts Grid -->
  <div class="services-charts-grid">
    <!-- Popular Services Chart -->
    <div class="popular-services-container">
      <div class="popular-services-card">
        <div class="card-header">
          <div class="header-content">
            <h3 class="card-title">Popular Services</h3>
            <ng-container
              *ngTemplateOutlet="
                infoIcon;
                context: { tooltip: 'Most requested car wash services by popularity.' }
              "
            ></ng-container>
          </div>
        </div>
        <div class="chart-container">
          @if (hasPopularServicesData) {
            <div class="bar-chart-wrapper">
              <canvas
                #popularServicesChart="base-chart"
                baseChart
                type="bar"
                [data]="popularServicesData"
                [options]="popularServicesOptions"
                class="bar-chart"
              >
              </canvas>
            </div>
          }
          <ng-container
            *ngTemplateOutlet="noDataMessage; context: { show: !hasPopularServicesData }"
          ></ng-container>
        </div>
      </div>
    </div>

    <!-- Service Trends Chart -->
    <div class="service-trends-container">
      <div class="service-trends-card">
        <div class="card-header">
          <div class="header-content">
            <h3 class="card-title">Service Trends</h3>
            <ng-container
              *ngTemplateOutlet="
                infoIcon;
                context: { tooltip: 'Service usage trends over time by service type.' }
              "
            ></ng-container>
          </div>
        </div>
        <div class="chart-container">
          @if (hasServiceTrendsData) {
            <div class="line-chart-wrapper">
              <canvas
                baseChart
                type="line"
                [data]="serviceTrendsData"
                [options]="serviceTrendsOptions"
                class="line-chart"
              >
              </canvas>
            </div>
          }
          <ng-container
            *ngTemplateOutlet="noDataMessage; context: { show: !hasServiceTrendsData }"
          ></ng-container>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Customers Tab Charts -->
<div
  class="customer-charts-container"
  [style.display]="activeTab === 'customers' ? 'block' : 'none'"
>
  <!-- Customer Charts Grid -->
  <div class="customer-charts-grid">
    <!-- Customer Growth Trend Chart -->
    <div class="customer-growth-container">
      <div class="customer-growth-card">
        <div class="card-header">
          <div class="header-content">
            <h3 class="card-title">Customer Growth Trend by Type</h3>
            <ng-container
              *ngTemplateOutlet="
                infoIcon;
                context: { tooltip: 'Track total customers vs new customers over time.' }
              "
            ></ng-container>
          </div>
        </div>
        <div class="chart-container">
          @if (hasCustomerGrowthData) {
            <div class="line-chart-wrapper">
              <canvas
                baseChart
                type="line"
                [data]="customerGrowthData"
                [options]="customerGrowthOptions"
                class="line-chart"
              >
              </canvas>
            </div>
          }
          <ng-container
            *ngTemplateOutlet="noDataMessage; context: { show: !hasCustomerGrowthData }"
          ></ng-container>
        </div>
      </div>
    </div>

    <!-- Customer Retention Rate Chart -->
    <div class="customer-retention-container">
      <div class="customer-retention-card">
        <div class="card-header">
          <div class="header-content">
            <h3 class="card-title">Customer Retention Rate</h3>
            <ng-container
              *ngTemplateOutlet="
                infoIcon;
                context: { tooltip: 'Percentage of customers who return for services.' }
              "
            ></ng-container>
          </div>
        </div>
        <div class="chart-container">
          @if (hasCustomerRetentionData) {
            <div class="line-chart-wrapper">
              <canvas
                baseChart
                type="line"
                [data]="customerRetentionData"
                [options]="customerRetentionOptions"
                class="line-chart"
              >
              </canvas>
            </div>
          }
          <ng-container
            *ngTemplateOutlet="noDataMessage; context: { show: !hasCustomerRetentionData }"
          ></ng-container>
        </div>
      </div>
    </div>

    <!-- Customer Breakdown Chart -->
    <div class="customer-breakdown-container">
      <div class="customer-breakdown-card">
        <div class="card-header">
          <div class="header-content">
            <h3 class="card-title">Customer Breakdown</h3>
            <ng-container
              *ngTemplateOutlet="
                infoIcon;
                context: { tooltip: 'Distribution of customers by type.' }
              "
            ></ng-container>
          </div>
        </div>
        <div class="chart-container">
          @if (hasCustomerBreakdownData) {
            <div class="donut-chart-wrapper">
              <canvas
                baseChart
                type="doughnut"
                [data]="customerBreakdownData"
                [options]="customerBreakdownOptions"
                class="donut-chart"
              >
              </canvas>
            </div>
          }
          <ng-container
            *ngTemplateOutlet="noDataMessage; context: { show: !hasCustomerBreakdownData }"
          ></ng-container>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Employees Tab Charts -->
<div
  class="employees-charts-container"
  [style.display]="activeTab === 'employees' ? 'block' : 'none'"
>
  <!-- Employees Charts Grid -->
  <div class="employees-charts-grid">
    <!-- Employee Performance Chart -->
    <div class="employee-performance-container">
      <div class="employee-performance-card">
        <div class="card-header">
          <div class="header-content">
            <h3 class="card-title">Employee Performance</h3>
            <ng-container
              *ngTemplateOutlet="
                infoIcon;
                context: {
                  tooltip: 'Performance scores for each employee based on efficiency and quality.',
                }
              "
            ></ng-container>
          </div>
        </div>
        <div class="chart-container">
          @if (hasEmployeePerformanceData) {
            <div class="bar-chart-wrapper">
              <canvas
                #employeePerformanceChart="base-chart"
                baseChart
                type="bar"
                [data]="employeePerformanceData"
                [options]="employeePerformanceOptions"
                class="bar-chart"
              >
              </canvas>
            </div>
          }
          <ng-container
            *ngTemplateOutlet="noDataMessage; context: { show: !hasEmployeePerformanceData }"
          ></ng-container>
        </div>
      </div>
    </div>

    <!-- Employee Hours Chart -->
    <div class="employee-hours-container">
      <div class="employee-hours-card">
        <div class="card-header">
          <div class="header-content">
            <h3 class="card-title">Employee Hours</h3>
            <ng-container
              *ngTemplateOutlet="
                infoIcon;
                context: {
                  tooltip: 'Total hours worked by each employee during the selected period.',
                }
              "
            ></ng-container>
          </div>
        </div>
        <div class="chart-container">
          @if (hasEmployeeHoursData) {
            <div class="bar-chart-wrapper">
              <canvas
                #employeeHoursChart="base-chart"
                baseChart
                type="bar"
                [data]="employeeHoursData"
                [options]="employeeHoursOptions"
                class="bar-chart"
              >
              </canvas>
            </div>
          }
          <ng-container
            *ngTemplateOutlet="noDataMessage; context: { show: !hasEmployeeHoursData }"
          ></ng-container>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Chart Info Icon Template (reused for all 9 chart card headers) -->
<ng-template #infoIcon let-tooltip="tooltip">
  <div class="info-icon" [matTooltip]="tooltip" matTooltipPosition="above" tabindex="0">
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M7 0C3.134 0 0 3.134 0 7s3.134 7 7 7 7-3.134 7-7-3.134-7-7-7zm0 12.833c-3.225 0-5.833-2.608-5.833-5.833S3.775 1.167 7 1.167 12.833 3.775 12.833 7 10.225 12.833 7 12.833z"
        fill="#15172E"
      />
      <path
        d="M7 3.5c-.483 0-.875.392-.875.875S6.517 5.25 7 5.25s.875-.392.875-.875S7.483 3.5 7 3.5zM7 6.125c-.483 0-.875.392-.875.875v2.625c0 .483.392.875.875.875s.875-.392.875-.875V7c0-.483-.392-.875-.875-.875z"
        fill="#15172E"
      />
    </svg>
  </div>
</ng-template>

<!-- No Data Message Template (reused for all 9 chart sections) -->
<ng-template #noDataMessage let-show="show">
  @if (show) {
    <div class="no-data-message">
      <div class="no-data-content">
        <svg
          class="warning-icon"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M12 2L1 21H23L12 2Z" stroke="#6B7280" stroke-width="2" stroke-linejoin="round" />
          <path d="M12 9V13" stroke="#6B7280" stroke-width="2" stroke-linecap="round" />
          <path d="M12 17H12.01" stroke="#6B7280" stroke-width="2" stroke-linecap="round" />
        </svg>
        <p class="no-data-text">No data available for the selected period.</p>
      </div>
    </div>
  }
</ng-template>
